import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, of, from } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Routing } from '@enums/routing.enum';
import { RateLimitService } from '../rate-limit/rate-limit.service';
import { TokenStorage } from './token-storage';
import type {
	AuthenticationResponseJSON,
	PublicKeyCredentialCreationOptionsJSON,
	PublicKeyCredentialRequestOptionsJSON,
	RegistrationResponseJSON,
} from '@simplewebauthn/browser';

/** A registered passkey as shown in Settings (metadata only — no secret material). */
export interface PasskeyInfo {
	/** Hex credential id — handle used to revoke it. */
	id: string;
	name: string | null;
	created_at: string;
}
import {
	AuthResponse,
	AuthState,
	initialAuthState,
	LoginRequest,
	LogoutRequest,
	RefreshRequest,
	RegisterRequest,
	RegisterResponse,
	TokenResponse,
	UserResponse,
	ForgotPasswordRequest,
	ResetPasswordRequest,
	VerifyEmailRequest,
	ResendVerificationRequest,
	MessageResponse,
	Setup2FARequest,
	Setup2FAResponse,
	Verify2FARequest,
	Disable2FARequest,
	Status2FAResponse,
	AuditLogsResponse,
	SecurityEventsResponse,
	SessionsResponse,
	LoginHistoryResponse,
	SecurityOverviewResponse,
	GdprDataExportResponse,
	GdprDeletionResponse,
	GdprConsentResponse,
	ChangePasswordRequest,
	UpdateProfileRequest,
	ChangeEmailRequest,
} from '@models/auth';

// Access token intentionally NOT persisted — kept in memory only.
// Refresh token lives in localStorage (see TokenStorage): shared by all tabs of the origin, so
// opening a link in a new tab keeps the session instead of bouncing to /login.
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly baseUrl = environment.API_URL;

	// Reactive state using signals
	private readonly _state = signal<AuthState>(this.loadInitialState());

	// Public computed signals
	readonly user = computed(() => this._state().user);
	readonly isAuthenticated = computed(() => this._state().isAuthenticated);
	readonly isLoading = computed(() => this._state().isLoading);
	readonly error = computed(() => this._state().error);
	readonly requires2FA = computed(() => this._state().requires2FA);
	readonly accessToken = computed(() => this._state().accessToken);

	private readonly http = inject(HttpClient);
	private readonly router = inject(Router);
	private readonly rateLimitService = inject(RateLimitService);

	/** Run (while the session still works) before an explicit logout — e.g. unlinking the phone from FCM. */
	private readonly _beforeLogout: Array<() => Promise<void>> = [];

	constructor() {
		this.listenForCrossTabLogout();
	}

	onBeforeLogout(hook: () => Promise<void>): void {
		this._beforeLogout.push(hook);
	}

	private runBeforeLogout(): Observable<unknown> {
		if (!this._beforeLogout.length || !this._state().isAuthenticated) {
			return of(undefined);
		}
		return from(Promise.allSettled(this._beforeLogout.map((h) => h())));
	}

	/**
	 * The session now lives in `localStorage`, i.e. it is SHARED by every tab (see TokenStorage).
	 * That is what makes "open in a new tab" keep you logged in — and it also means a logout in one
	 * tab has to reach the others, otherwise they would keep a dead session in memory until reload.
	 * The `storage` event fires only in the OTHER tabs, so this is exactly the hand-over we need.
	 */
	private listenForCrossTabLogout(): void {
		if (typeof window === 'undefined') {
			return;
		}

		window.addEventListener('storage', (event: StorageEvent) => {
			if (event.storageArea !== localStorage || event.key !== REFRESH_TOKEN_KEY) {
				return;
			}

			// Token removed elsewhere (logout / forced logout) → drop this tab's session too.
			if (event.newValue === null && this._state().isAuthenticated) {
				this.updateState(initialAuthState);
				this.router.navigate([Routing.PanelLogin]);
				return;
			}

			// Token rotated elsewhere → adopt it, so this tab does not refresh with a stale one.
			// (A fresh login in another tab is NOT adopted here: this tab picks the session up on its
			// next navigation/reload, which is when the guard runs anyway.)
			if (
				event.newValue &&
				this._state().isAuthenticated &&
				event.newValue !== this._state().refreshToken
			) {
				this.updateState({ refreshToken: event.newValue });
			}
		});
	}

	// === Auth Actions ===

	login(request: LoginRequest): Observable<AuthResponse> {
		this.updateState({ isLoading: true, error: null });

		return this.http.post<AuthResponse>(`${this.baseUrl}auth/login`, request).pipe(
			tap((response) => this.handleAuthSuccess(response)),
			catchError((error) => this.handleAuthError(error)),
		);
	}

	/** Quick-unlock: enroll a PIN / biometric secret on this device (needs an authenticated session). */
	enrollQuickUnlock(
		deviceId: string,
		method: 'pin' | 'biometric',
		secret: string,
	): Observable<{ enrolled: boolean }> {
		return this.http.post<{ enrolled: boolean }>(`${this.baseUrl}auth/quick-unlock/enroll`, {
			device_id: deviceId,
			method,
			secret,
		});
	}

	/** Quick-unlock: log in with a PIN / biometric secret on a trusted device. */
	quickUnlockLogin(
		email: string,
		deviceId: string,
		method: 'pin' | 'biometric',
		secret: string,
	): Observable<AuthResponse> {
		this.updateState({ isLoading: true, error: null });
		return this.http
			.post<AuthResponse>(`${this.baseUrl}auth/quick-unlock/login`, {
				email,
				device_id: deviceId,
				method,
				secret,
			})
			.pipe(
				tap((response) => this.handleAuthSuccess(response)),
				catchError((error) => this.handleAuthError(error)),
			);
	}

	// ── WebAuthn / passkeys (Faza 4) ──────────────────────────────────────────
	webauthnRegisterStart(): Observable<{ publicKey: PublicKeyCredentialCreationOptionsJSON }> {
		return this.http.post<{ publicKey: PublicKeyCredentialCreationOptionsJSON }>(
			`${this.baseUrl}auth/webauthn/register/start`,
			{},
		);
	}
	webauthnRegisterFinish(cred: RegistrationResponseJSON): Observable<{ registered: boolean }> {
		return this.http.post<{ registered: boolean }>(
			`${this.baseUrl}auth/webauthn/register/finish`,
			cred,
		);
	}
	webauthnLoginStart(
		email: string,
	): Observable<{ publicKey: PublicKeyCredentialRequestOptionsJSON }> {
		return this.http.post<{ publicKey: PublicKeyCredentialRequestOptionsJSON }>(
			`${this.baseUrl}auth/webauthn/login/start`,
			{ email },
		);
	}
	webauthnLoginFinish(
		email: string,
		credential: AuthenticationResponseJSON,
	): Observable<AuthResponse> {
		this.updateState({ isLoading: true, error: null });
		return this.http
			.post<AuthResponse>(`${this.baseUrl}auth/webauthn/login/finish`, { email, credential })
			.pipe(
				tap((response) => this.handleAuthSuccess(response)),
				catchError((error) => this.handleAuthError(error)),
			);
	}

	webauthnListPasskeys(): Observable<PasskeyInfo[]> {
		return this.http.get<PasskeyInfo[]>(`${this.baseUrl}auth/webauthn/passkeys`);
	}
	webauthnDeletePasskey(id: string): Observable<{ deleted: boolean }> {
		return this.http.delete<{ deleted: boolean }>(
			`${this.baseUrl}auth/webauthn/passkeys/${id}`,
		);
	}

	register(request: RegisterRequest): Observable<RegisterResponse> {
		this.updateState({ isLoading: true, error: null });

		return this.http.post<RegisterResponse>(`${this.baseUrl}auth/register`, request).pipe(
			tap(() => this.updateState({ isLoading: false })),
			catchError((error) => this.handleAuthError(error)),
		);
	}

	logout(): Observable<void> {
		const refreshToken = this._state().refreshToken;

		if (!refreshToken) {
			this.clearAuthState();
			return of(undefined);
		}

		const request: LogoutRequest = { refresh_token: refreshToken };

		return this.runBeforeLogout().pipe(
			switchMap(() => this.http.post<void>(`${this.baseUrl}auth/logout`, request)),
			tap(() => this.clearAuthState()),
			catchError(() => {
				this.clearAuthState();
				return of(undefined);
			}),
		);
	}

	logoutAll(): Observable<void> {
		const refreshToken = this._state().refreshToken;

		if (!refreshToken) {
			this.clearAuthState();
			return of(undefined);
		}

		const request: LogoutRequest = { refresh_token: refreshToken };

		return this.runBeforeLogout().pipe(
			switchMap(() => this.http.post<void>(`${this.baseUrl}auth/logout-all`, request)),
			tap(() => this.clearAuthState()),
			catchError(() => {
				this.clearAuthState();
				return of(undefined);
			}),
		);
	}

	refreshToken(): Observable<TokenResponse> {
		const refreshToken = this._state().refreshToken;

		if (!refreshToken) {
			return throwError(() => new Error('No refresh token available'));
		}

		const request: RefreshRequest = { refresh_token: refreshToken };

		return this.http.post<TokenResponse>(`${this.baseUrl}auth/refresh`, request).pipe(
			tap((response) => {
				this.updateState({
					accessToken: response.access_token,
					refreshToken: response.refresh_token,
				});
				this.saveTokens(response.access_token, response.refresh_token);
			}),
			catchError((error: HttpErrorResponse) => {
				// Only clear auth state when the token is genuinely invalid/expired.
				// Do NOT log out the user for transient errors (429 rate-limit, 5xx, network).
				if (error.status === 401 || error.status === 403) {
					this.clearAuthState();
				}

				return throwError(() => error);
			}),
		);
	}

	// === Email Verification ===

	requestEmailVerification(): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(
			`${this.baseUrl}auth/email/request-verification`,
			{},
		);
	}

	verifyEmail(request: VerifyEmailRequest): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(`${this.baseUrl}auth/verify-email`, request);
	}

	resendVerification(request: ResendVerificationRequest): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(`${this.baseUrl}auth/resend-verification`, request);
	}

	// === Password Reset ===

	forgotPassword(request: ForgotPasswordRequest): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(`${this.baseUrl}auth/forgot-password`, request);
	}

	resetPassword(request: ResetPasswordRequest): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(`${this.baseUrl}auth/reset-password`, request);
	}

	// === 2FA ===

	setup2FA(request: Setup2FARequest): Observable<Setup2FAResponse> {
		return this.http.post<Setup2FAResponse>(`${this.baseUrl}auth/2fa/setup`, request);
	}

	verify2FA(request: Verify2FARequest): Observable<Status2FAResponse> {
		return this.http.post<Status2FAResponse>(`${this.baseUrl}auth/2fa/verify`, request).pipe(
			tap(() => {
				// Update user state to reflect 2FA enabled
				const currentUser = this._state().user;

				if (currentUser) {
					this.updateState({
						user: { ...currentUser, totp_enabled: true },
					});
					this.saveUser({ ...currentUser, totp_enabled: true });
				}
			}),
		);
	}

	disable2FA(request: Disable2FARequest): Observable<Status2FAResponse> {
		return this.http.post<Status2FAResponse>(`${this.baseUrl}auth/2fa/disable`, request).pipe(
			tap(() => {
				const currentUser = this._state().user;

				if (currentUser) {
					this.updateState({
						user: { ...currentUser, totp_enabled: false },
					});
					this.saveUser({ ...currentUser, totp_enabled: false });
				}
			}),
		);
	}

	get2FAStatus(): Observable<Status2FAResponse> {
		return this.http.get<Status2FAResponse>(`${this.baseUrl}auth/2fa/status`);
	}

	// === User Profile ===

	getMe(): Observable<UserResponse> {
		return this.http.get<UserResponse>(`${this.baseUrl}user/me`).pipe(
			tap((user) => {
				this.updateState({ user });
				this.saveUser(user);
			}),
		);
	}

	changePassword(request: ChangePasswordRequest): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(`${this.baseUrl}user/change-password`, request);
	}

	updateProfile(request: UpdateProfileRequest): Observable<UserResponse> {
		return this.http.patch<UserResponse>(`${this.baseUrl}user/profile`, request).pipe(
			tap((user) => {
				this.updateState({ user });
				this.saveUser(user);
			}),
		);
	}

	requestEmailChange(request: ChangeEmailRequest): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(`${this.baseUrl}user/change-email`, request);
	}

	// === Account Type & Business Profile ===

	updateAccountType(accountType: 'private' | 'business'): Observable<MessageResponse> {
		return this.http
			.put<MessageResponse>(`${this.baseUrl}user/account-type`, {
				account_type: accountType,
			})
			.pipe(
				tap(() => {
					const currentUser = this._state().user;

					if (currentUser) {
						const updated = { ...currentUser, account_type: accountType };
						this.updateState({ user: updated });
						this.saveUser(updated);
					}
				}),
			);
	}

	getBusinessProfile(): Observable<import('@models/auth').BusinessProfile> {
		return this.http.get<import('@models/auth').BusinessProfile>(
			`${this.baseUrl}user/business-profile`,
		);
	}

	upsertBusinessProfile(
		request: import('@models/auth').UpsertBusinessProfileRequest,
	): Observable<import('@models/auth').BusinessProfile> {
		return this.http.put<import('@models/auth').BusinessProfile>(
			`${this.baseUrl}user/business-profile`,
			request,
		);
	}

	deleteBusinessProfile(): Observable<MessageResponse> {
		return this.http.delete<MessageResponse>(`${this.baseUrl}user/business-profile`);
	}

	// === Investment Profile ===

	getInvestmentProfile(): Observable<import('@models/auth').InvestmentProfile> {
		return this.http.get<import('@models/auth').InvestmentProfile>(
			`${this.baseUrl}user/investment-profile`,
		);
	}

	upsertInvestmentProfile(
		request: import('@models/auth').UpsertInvestmentProfileRequest,
	): Observable<import('@models/auth').InvestmentProfile> {
		return this.http.put<import('@models/auth').InvestmentProfile>(
			`${this.baseUrl}user/investment-profile`,
			request,
		);
	}

	// === Security/Audit ===

	getAuditLogs(limit: number = 50, offset: number = 0): Observable<AuditLogsResponse> {
		return this.http.get<AuditLogsResponse>(
			`${this.baseUrl}user/audit-logs?limit=${limit}&offset=${offset}`,
		);
	}

	getSecurityEvents(): Observable<SecurityEventsResponse> {
		return this.http.get<SecurityEventsResponse>(`${this.baseUrl}user/security-events`);
	}

	// === Sessions Management ===

	getActiveSessions(): Observable<SessionsResponse> {
		return this.http.get<SessionsResponse>(`${this.baseUrl}user/sessions`);
	}

	revokeSession(sessionId: string): Observable<{ message: string }> {
		return this.http.delete<{ message: string }>(`${this.baseUrl}user/sessions/${sessionId}`);
	}

	// === Login History ===

	getLoginHistory(): Observable<LoginHistoryResponse> {
		return this.http.get<LoginHistoryResponse>(`${this.baseUrl}user/login-history`);
	}

	// === Security Overview ===

	getSecurityOverview(): Observable<SecurityOverviewResponse> {
		return this.http.get<SecurityOverviewResponse>(`${this.baseUrl}user/security-overview`);
	}

	// === GDPR Compliance ===

	requestDataExport(): Observable<GdprDataExportResponse> {
		return this.http.get<GdprDataExportResponse>(`${this.baseUrl}user/gdpr/export`);
	}

	requestAccountDeletion(reason?: string): Observable<GdprDeletionResponse> {
		return this.http.post<GdprDeletionResponse>(`${this.baseUrl}user/gdpr/delete`, { reason });
	}

	cancelAccountDeletion(): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(`${this.baseUrl}user/gdpr/cancel-deletion`, {});
	}

	getGdprConsent(): Observable<GdprConsentResponse> {
		return this.http.get<GdprConsentResponse>(`${this.baseUrl}user/gdpr/consent`);
	}

	recordGdprConsent(marketingConsent: boolean): Observable<GdprConsentResponse> {
		return this.http.post<GdprConsentResponse>(`${this.baseUrl}user/gdpr/consent`, {
			marketing_consent: marketingConsent,
		});
	}

	// === Token Management ===

	getAccessToken(): string | null {
		return this._state().accessToken;
	}

	isTokenExpired(): boolean {
		const token = this._state().accessToken;

		if (!token) {
			return true;
		}

		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			const exp = payload.exp * 1000; // Convert to milliseconds

			return Date.now() > exp - 30000;
		} catch {
			return true;
		}
	}

	// === State Management Helpers ===

	private handleAuthSuccess(response: AuthResponse): void {
		this.updateState({
			user: response.user,
			accessToken: response.access_token,
			refreshToken: response.refresh_token,
			isAuthenticated: true,
			isLoading: false,
			error: null,
			requires2FA: false,
		});

		this.saveTokens(response.access_token, response.refresh_token);
		this.saveUser(response.user);
	}

	getRetryTimeForEndpoint(endpoint: string): number {
		return this.rateLimitService.getTimeUntilReset(endpoint);
	}

	isEndpointRateLimited(endpoint: string): boolean {
		return this.rateLimitService.isEndpointRateLimited(endpoint);
	}

	private handleAuthError(error: HttpErrorResponse): Observable<never> {
		let errorMessage = 'auth.error.unknown';

		if (error.status === 401) {
			if (error.error?.code === '2FA_REQUIRED') {
				this.updateState({
					isLoading: false,
					requires2FA: true,
					error: null,
				});
				return throwError(() => ({ ...error, requires2FA: true }));
			}

			errorMessage = 'auth.error.invalid-credentials';
		} else if (error.status === 403) {
			if (error.error?.error === 'EMAIL_NOT_VERIFIED') {
				errorMessage = 'auth.error.email-not-verified';
			} else {
				errorMessage = 'auth.error.account-locked';
			}
		} else if (error.status === 409) {
			errorMessage = 'auth.error.email-exists';
		} else if (error.status === 423) {
			errorMessage = 'auth.error.account-locked';
		} else if (error.status === 429) {
			errorMessage = 'auth.error.too-many-attempts';
		} else if (error.status === 400) {
			errorMessage = error.error?.error || 'auth.error.validation';
		}

		this.updateState({
			isLoading: false,
			error: errorMessage,
		});

		return throwError(() => error);
	}

	forceLogout(): void {
		this.updateState(initialAuthState);
		this.clearStorage();
		this.router.navigate([Routing.PanelLogin]);
	}

	private clearAuthState(): void {
		this.updateState(initialAuthState);
		this.clearStorage();
		this.router.navigate([Routing.PanelLogin]);
	}

	private updateState(partial: Partial<AuthState>): void {
		const newState = { ...this._state(), ...partial };
		this._state.set(newState);
	}

	// === Storage ===

	private loadInitialState(): AuthState {
		// Access token is never persisted — only refresh token survives page reloads.
		// On next authenticated API call, the interceptor triggers a silent refresh.
		const refreshToken = TokenStorage.get(REFRESH_TOKEN_KEY);
		const userJson = TokenStorage.get(USER_KEY);

		if (refreshToken && userJson) {
			try {
				const user = JSON.parse(userJson) as UserResponse;
				return {
					user,
					accessToken: null,
					refreshToken,
					isAuthenticated: true,
					isLoading: false,
					error: null,
					requires2FA: false,
				};
			} catch {
				this.clearStorage();
			}
		}

		return initialAuthState;
	}

	private saveTokens(accessToken: string, refreshToken: string): void {
		// Access token stays in memory only (XSS protection).
		void accessToken;
		// Refresh token → localStorage on both web and native: shared across tabs, survives a
		// restart, and cleared in every tab on logout via the `storage` listener. See TokenStorage.
		TokenStorage.set(REFRESH_TOKEN_KEY, refreshToken);
	}

	private saveUser(user: UserResponse): void {
		TokenStorage.set(USER_KEY, JSON.stringify(user));
	}

	private clearStorage(): void {
		TokenStorage.remove(REFRESH_TOKEN_KEY);
		TokenStorage.remove(USER_KEY);
		// Sweep an access token that old versions used to persist (never written any more).
		localStorage.removeItem('access_token');
	}
}
